import type { IconProp } from "@fortawesome/fontawesome-svg-core";
import { Button, Dialog, Typography, cn, useModal } from "@tosize/ui";
import type { ComponentProps, ReactNode } from "react";
import { FormattedMessage } from "react-intl";

interface TitleBlockProps {
	title: string;
	label?: {
		title: string;
		onClick?: ComponentProps<"button">["onClick"];
	};
	explanation?: ReactNode;
	border?: boolean;
	className?: string;
	action?: {
		title: string;
		icon?: IconProp;
		isLoading?: boolean;
		disabled?: boolean;
		onClick: ComponentProps<"button">["onClick"];
	};
}

export default function TitleBlock(props: TitleBlockProps) {
	const { title, label, action, explanation, border, className } = props;
	const modal = useModal();

	return (
		<div
			className={cn("flex justify-between gap-2", className, { "border-shades border-b": border })}
		>
			<div className="flex flex-wrap items-center gap-x-2 gap-y-1">
				<Typography variant="paragraph-bold" component="span">
					{title}
				</Typography>

				{label?.onClick ? (
					<button
						type="button"
						className="appearance-none whitespace-nowrap rounded-xl bg-secondary enter:bg-secondary-medium px-1.5 py-0.5 font-bold font-sans text-white text-xs leading-none no-underline focus:outline-none motion-safe:transition-colors"
						{...label}
					>
						{label.title}
					</button>
				) : (
					!!label?.title && (
						<span className="rounded-xl bg-secondary px-1.5 py-0.5 font-bold font-sans text-white text-xs leading-none no-underline">
							{label.title}
						</span>
					)
				)}
			</div>

			{!!explanation && (
				<div className="ml-auto">
					<Button
						variant="link"
						className="rounded enter:bg-shades-medium-light px-2 py-1 text-right text-sm leading-none enter:no-underline"
						onClick={modal.toggle}
					>
						<FormattedMessage defaultMessage="Meer informatie" id="Mv6ZTa" />
					</Button>

					<Dialog {...modal.props} width="2xl" title={title}>
						<span className="prose">{explanation}</span>
					</Dialog>
				</div>
			)}

			{action && (
				<Button
					variant="link"
					endicon={action.icon}
					onClick={action.onClick}
					disabled={action.disabled}
					isLoading={action.isLoading}
					className="whitespace-nowrap rounded enter:bg-shades-medium-light px-2 text-sm enter:no-underline"
				>
					{action.title}
				</Button>
			)}
		</div>
	);
}
